import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format seconds to MM:SS or HH:MM:SS
 */
export function formatTime(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) return '0:00'
  
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format remaining time as negative
 */
export function formatRemainingTime(currentTime: number, duration: number): string {
  const remaining = duration - currentTime
  return `-${formatTime(remaining)}`
}

/**
 * Find segment index at given time
 */
export function findSegmentAtTime(
  segments: Array<{ start: number; end: number }>,
  time: number
): number {
  return segments.findIndex(
    seg => time >= seg.start && time < seg.end
  )
}

/**
 * Find word index at given time within a segment
 */
export function findWordAtTime(
  words: Array<{ start: number; end: number }> | null | undefined,
  time: number
): number {
  if (!words) return -1
  return words.findIndex(
    word => time >= word.start && time < word.end
  )
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * Calculate progress percentage
 */
export function calculateProgress(currentTime: number, duration: number): number {
  if (duration <= 0) return 0
  return Math.min((currentTime / duration) * 100, 100)
}

/**
 * Parse ISO date string to locale string
 */
export function formatDate(isoString: string, locale: string = 'tr-TR'): string {
  try {
    const date = new Date(isoString)
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  } catch {
    return isoString
  }
}

/**
 * Format duration as human readable
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  if (mins < 60) {
    return `${mins} dk`
  }
  const hrs = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return `${hrs} sa ${remainingMins} dk`
}
